import React, { useState, useRef } from 'react';
import getValue from 'lodash.get';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DocumentsPage from './components/DocumentsPage.jsx';
import SectionContainer from '../layout/SectionContainer.jsx';
import SectionNav from '../layout/SectionNav.jsx';
import WelcomeScreen from '../layout/WelcomeScreen.jsx';
import ContentContainer from '../layout/ContentContainer.jsx';
import { getTextDefaults } from './translations/backgroundCheckTranslations.js';
import uploadFile from '../util/upload.js';
import { DEFAULT_ACCEPT_STRING, getAcceptedExtensions } from '../util/acceptedFormats.js';

const GAP_THRESHOLD_DAYS = 28;

function computeAdultCutoff(dateOfBirth) {
	if (!dateOfBirth) return null;
	const dob = new Date(dateOfBirth);
	if (isNaN(dob.getTime())) return null;
	return new Date(dob.getFullYear() + 18, dob.getMonth(), dob.getDate());
}

function buildTimeline(employmentPeriods, cutoffDate) {
	if (!Array.isArray(employmentPeriods) || employmentPeriods.length === 0) return null;

	const now = new Date();
	const parsed = employmentPeriods
		.map((period) => {
			const start = new Date(period.startDate);
			const end = period.endDate === 'present' || period.endDate === 'Present' ? now : new Date(period.endDate);
			if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
			return { start, end, company: period.company };
		})
		.filter(Boolean)
		.sort((a, b) => a.start - b.start);

	if (parsed.length === 0) return null;

	const merged = [{ start: parsed[0].start, end: parsed[0].end, companies: [parsed[0].company] }];
	for (let i = 1; i < parsed.length; i++) {
		const last = merged[merged.length - 1];
		if (parsed[i].start <= last.end) {
			if (parsed[i].end > last.end) last.end = parsed[i].end;
			if (!last.companies.includes(parsed[i].company)) last.companies.push(parsed[i].company);
		} else {
			merged.push({ start: parsed[i].start, end: parsed[i].end, companies: [parsed[i].company] });
		}
	}

	const fmt = (d) => d.toLocaleString('en', { month: 'short', year: 'numeric' });
	const isPresent = (d) => Math.abs(d - now) < 1000 * 60 * 60 * 24 * 2;

	const entries = [];
	for (let i = 0; i < merged.length; i++) {
		const minor = cutoffDate && merged[i].end <= cutoffDate;
		entries.push({
			type: 'employment',
			companies: merged[i].companies,
			start: merged[i].start,
			end: merged[i].end,
			minor,
			label: `${fmt(merged[i].start)} — ${isPresent(merged[i].end) ? 'Present' : fmt(merged[i].end)}`,
		});

		if (i < merged.length - 1) {
			const gapStart = merged[i].end;
			const gapEnd = merged[i + 1].start;
			const gapDays = (gapEnd - gapStart) / (1000 * 60 * 60 * 24);
			// Only show gaps after the person turned 18
			const gapBeforeCutoff = cutoffDate && gapEnd <= cutoffDate;
			if (gapDays > GAP_THRESHOLD_DAYS && !gapBeforeCutoff) {
				const months = Math.round(gapDays / 30);
				entries.push({
					type: 'gap',
					id: `gap-${i}`,
					start: gapStart,
					end: gapEnd,
					months,
					label: `${fmt(gapStart)} — ${fmt(gapEnd)} (${months} month${months !== 1 ? 's' : ''})`,
				});
			}
		}
	}

	entries.reverse();
	return entries.some((e) => e.type === 'gap') ? entries : null;
}

function getTimelineData(cvDoc, dateOfBirth) {
	const raw = cvDoc?.metadata?.raw;
	if (!raw) return null;
	const periods = raw.employmentPeriods || raw.extractedData?.employmentPeriods;
	const cutoff = computeAdultCutoff(dateOfBirth);
	return buildTimeline(periods, cutoff);
}

function getDateFormatFeedback(cvDoc) {
	const raw = cvDoc?.metadata?.raw;
	if (!raw) return null;
	const feedbackText = raw.dateFormatViolationsFeedback || raw.extractedData?.dateFormatViolationsFeedback || '';
	return feedbackText.trim().length > 0 ? feedbackText : null;
}

const WelcomePage = ({ text }) => (
	<WelcomeScreen>
		<Typography variant="h6">{text.welcomeTitle}</Typography>
		<Typography>{text.welcomeReason}</Typography>
	</WelcomeScreen>
);

const CVUploadPage = ({ values, onInput, text, uploadUrl, disabled, useCamera, cameraIncludes, cameraRotationAngle }) => {
	const userDocuments = values.userDocuments || [];
	const cvDoc = userDocuments.find((doc) => doc?.type === 'cv');
	const criminalRecordDoc = userDocuments.find((doc) => doc?.type === 'model-596-1');
	const cvUploadDocs = [cvDoc || null, criminalRecordDoc || null];
	const uploadContext = {
		userData: {
			firstName: getValue(values, 'userData.firstName') || getValue(values, 'userProps.firstName'),
			lastName: getValue(values, 'userData.lastName') || getValue(values, 'userProps.lastName'),
		},
	};

	return (
		<ContentContainer>
			<Typography variant="h6" sx={{ marginBottom: 2 }}>{text.cvUploadTitle}</Typography>
			<Typography variant="body1" sx={{ marginBottom: 3 }}>{text.cvUploadText}</Typography>
			<DocumentsPage
				requiredDocuments={['cv', 'model-596-1']}
				labels={{ 'cv': 'CV', 'model-596-1': text.criminalRecordLabel }}
				onChange={onInput}
				uploadUrl={uploadUrl}
				value={cvUploadDocs}
				text={text}
				name="userDocuments"
				disabled={disabled}
				useCamera={useCamera}
				cameraIncludes={cameraIncludes}
				cameraRotationAngle={cameraRotationAngle}
				context={uploadContext}
			/>
		</ContentContainer>
	);
};

const DateFormatWarningPage = ({ text, dateFormatFeedback }) => (
	<ContentContainer>
		<Typography variant="h6" sx={{ marginBottom: 2 }}>{text.dateFormatWarningTitle}</Typography>
		<Typography variant="body1" sx={{ marginBottom: 3 }}>{text.dateFormatWarningText}</Typography>
		<Typography
			variant="body2"
			sx={{
				padding: 2,
				backgroundColor: 'rgba(255, 152, 0, 0.08)',
				borderRadius: 1,
				color: 'text.primary',
				whiteSpace: 'pre-line',
			}}
		>
			{dateFormatFeedback}
		</Typography>
	</ContentContainer>
);

const formatISODate = (d) => d.toISOString().split('T')[0];

const GAP_PROOF_ACCEPTED_FORMATS = getAcceptedExtensions(DEFAULT_ACCEPT_STRING);

const GapProofUpload = ({ gapEntry, uploadUrl, proof, onUpload, disabled, text }) => {
	const fileInputRef = useRef(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	const handleFileChange = async (e) => {
		const file = e.target.files[0];
		if (!file) return;
		setLoading(true);
		setError(null);
		try {
			const uploaded = await uploadFile(uploadUrl, file, false, {
				contentType: file.type,
				type: 'cv-gap-proof',
				filename: `Gap proof - ${gapEntry.id}`,
				context: {
					gapPeriod: {
						startDate: formatISODate(gapEntry.start),
						endDate: formatISODate(gapEntry.end),
					},
				},
			});
			console.log('[BackgroundCheck] Gap proof upload result:', JSON.stringify(uploaded, null, 2));
			onUpload(gapEntry.id, uploaded);
		} catch (err) {
			setError(err?.message || String(err));
		} finally {
			setLoading(false);
		}
	};

	if (proof) {
		const meta = proof.metadata || {};
		const raw = meta.raw || {};
		const description = meta.documentDescription || raw.documentDescription || proof.fileName || text.gapProofUploaded;
		const isDeclined = proof.status === 'declined' || meta.gapProofStatus === 'declined';
		const rejectionReasons = meta.gapProofRejectionReasons || raw.gapProofRejectionReasons || [];
		const coverageFeedback = meta.gapPeriodCoverageFeedback || raw.gapPeriodCoverageFeedback;
		const coversGap = meta.coversGapPeriod ?? raw.coversGapPeriod;

		if (isDeclined) {
			return (
				<Box sx={{ mt: 1 }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<ErrorOutlineIcon sx={{ color: 'error.main', fontSize: 20 }} />
						<Typography variant="body2" sx={{ fontWeight: 500, color: 'error.main' }}>
							{description}
						</Typography>
					</Box>
					{rejectionReasons.length > 0 && (
						<Box sx={{ mt: 0.5, ml: 3.5 }}>
							{rejectionReasons.map((reason, i) => (
								<Typography key={i} variant="caption" sx={{ display: 'block', color: 'error.main' }}>
									{reason}
								</Typography>
							))}
						</Box>
					)}
					<Typography variant="caption" sx={{ display: 'block', mt: 1, ml: 3.5, color: 'text.secondary' }}>
						{text.gapProofDeclinedRetry}
					</Typography>
					<Box sx={{ mt: 1 }}>
						<input
							ref={fileInputRef}
							type="file"
							accept={DEFAULT_ACCEPT_STRING}
							style={{ display: 'none' }}
							onChange={handleFileChange}
						/>
						<Box
							onClick={() => !loading && !disabled && fileInputRef.current?.click()}
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 1,
								cursor: disabled || loading ? 'default' : 'pointer',
								color: 'primary.main',
								'&:hover': { opacity: disabled || loading ? 1 : 0.7 },
							}}
						>
							{loading ? <CircularProgress size={18} /> : <FileUploadIcon sx={{ fontSize: 20 }} />}
							<Typography variant="body2">{loading ? text.uploading : text.uploadDifferentDocument}</Typography>
						</Box>
						<Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
							({GAP_PROOF_ACCEPTED_FORMATS.join(', ')})
						</Typography>
						{error && (
							<Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
								{error}
							</Typography>
						)}
					</Box>
				</Box>
			);
		}

		const isInReview = proof.status === 'inReview' || meta.gapProofStatus === 'inReview';

		return (
			<Box sx={{ mt: 1 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					{isInReview
						? <ScheduleIcon sx={{ color: 'warning.main', fontSize: 20 }} />
						: <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
					}
					<Typography variant="body2" sx={{ fontWeight: 500 }}>
						{text.gapProofUploaded}
					</Typography>
				</Box>
				{isInReview && (
					<Typography
						variant="caption"
						sx={{ display: 'block', mt: 0.5, ml: 3.5, color: 'warning.dark' }}
					>
						{text.gapProofInReview}
					</Typography>
				)}
			</Box>
		);
	}

	return (
		<Box sx={{ mt: 1 }}>
			<input
				ref={fileInputRef}
				type="file"
				accept={DEFAULT_ACCEPT_STRING}
				style={{ display: 'none' }}
				onChange={handleFileChange}
			/>
			<Box
				onClick={() => !loading && !disabled && fileInputRef.current?.click()}
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: 1,
					cursor: disabled || loading ? 'default' : 'pointer',
					color: 'primary.main',
					'&:hover': { opacity: disabled || loading ? 1 : 0.7 },
				}}
			>
				{loading ? <CircularProgress size={18} /> : <FileUploadIcon sx={{ fontSize: 20 }} />}
				<Typography variant="body2">{loading ? text.uploading : text.uploadGapProof}</Typography>
			</Box>
			<Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
				({GAP_PROOF_ACCEPTED_FORMATS.join(', ')})
			</Typography>
			{error && (
				<Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
					{error}
				</Typography>
			)}
		</Box>
	);
};

function findGapProof(userDocuments, gapEntry) {
	const gapStart = formatISODate(gapEntry.start);
	const gapEnd = formatISODate(gapEntry.end);
	return userDocuments.find(
		(doc) =>
			doc?.type === 'cv-gap-proof' &&
			doc?.context?.gapPeriod?.startDate === gapStart &&
			doc?.context?.gapPeriod?.endDate === gapEnd,
	);
}

function isGapProofAccepted(proof) {
	if (!proof) return false;
	return proof.status !== 'declined' && proof.metadata?.gapProofStatus !== 'declined';
}

const GapTimelinePage = ({ text, values, onChange, uploadUrl, disabled, timelineData }) => {
	const userDocuments = values.userDocuments || [];

	const handleGapProofUpload = (gapId, uploadedFile) => {
		const gapEntry = timelineData.find((e) => e.id === gapId);
		if (!gapEntry) {
			onChange({ target: { name: 'userDocuments', value: [...userDocuments, uploadedFile] } });
			return;
		}
		const gapStart = formatISODate(gapEntry.start);
		const gapEnd = formatISODate(gapEntry.end);
		const filtered = userDocuments.filter(
			(doc) =>
				!(doc?.type === 'cv-gap-proof' &&
				doc?.context?.gapPeriod?.startDate === gapStart &&
				doc?.context?.gapPeriod?.endDate === gapEnd),
		);
		onChange({ target: { name: 'userDocuments', value: [...filtered, uploadedFile] } });
	};

	return (
		<ContentContainer>
			<Typography variant="h6" sx={{ marginBottom: 2 }}>{text.gapClarificationTitle}</Typography>
			<Typography variant="body1" sx={{ marginBottom: 3 }}>{text.gapClarificationText}</Typography>
			<Box sx={{ position: 'relative', pl: 4, pb: 1 }}>
				<Box
					sx={{
						position: 'absolute',
						left: 11,
						top: 8,
						bottom: 8,
						width: 2,
						backgroundColor: 'divider',
					}}
				/>
				{timelineData.map((entry, i) => (
					<Box key={entry.id || `emp-${i}`} sx={{ position: 'relative', mb: 3, '&:last-child': { mb: 0 } }}>
						<Box
							sx={{
								position: 'absolute',
								left: -28,
								top: 4,
								width: 12,
								height: 12,
								borderRadius: '50%',
								backgroundColor: entry.type === 'gap' ? 'warning.main' : entry.minor ? 'grey.400' : 'success.main',
								zIndex: 1,
							}}
						/>
						{entry.type === 'employment' ? (
							<Box sx={{ minHeight: 36, opacity: entry.minor ? 0.55 : 1 }}>
								<Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
									{entry.companies.join(', ')}
								</Typography>
								<Typography variant="caption" color="text.secondary">
									{entry.label}
								</Typography>
								{entry.minor && (
									<Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic', color: 'text.disabled' }}>
										{text.beforeAge18}
									</Typography>
								)}
							</Box>
						) : (
							<Card
								variant="outlined"
								sx={{
									p: 2,
									borderColor: 'warning.main',
									borderStyle: 'dashed',
									backgroundColor: 'rgba(255, 152, 0, 0.04)',
								}}
							>
								<Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'warning.dark' }}>
									{text.employmentGap}
								</Typography>
								<Typography variant="caption" color="text.secondary">
									{entry.label}
								</Typography>
								<GapProofUpload
									gapEntry={entry}
									uploadUrl={uploadUrl}
									proof={findGapProof(userDocuments, entry)}
									onUpload={handleGapProofUpload}
									disabled={disabled}
									text={text}
								/>
							</Card>
						)}
					</Box>
				))}
			</Box>
		</ContentContainer>
	);
};

const CompletionPage = ({ text }) => (
	<WelcomeScreen>
		<Typography variant="h6" sx={{ marginBottom: 2 }}>{text.completionTitle}</Typography>
		<Typography>{text.completionText}</Typography>
	</WelcomeScreen>
);

const BackgroundCheckSection = ({
	Registry,
	onChange = () => {},
	values = {},
	textOverride = {},
	uploadUrl = '/api/files',
	background = 'light',
	selectedLanguage = 'EN',
	addPendingAction = () => {},
	removePendingAction = () => {},
	useCamera = true,
	cameraIncludes = '',
	cameraRotationAngle = 0,
	disabled = false,
	navigationProps,
}) => {
	const text = Object.assign({}, getTextDefaults(selectedLanguage), textOverride);
	const [currentScreen, setCurrentScreen] = useState('welcome');
	const [timelineData, setTimelineData] = useState(null);
	const [dateFormatFeedback, setDateFormatFeedback] = useState(null);

	const userDocuments = values.userDocuments || [];
	const cvDocument = userDocuments.find((doc) => doc?.type === 'cv');
	const criminalRecordDocument = userDocuments.find((doc) => doc?.type === 'model-596-1');

	const dateOfBirth = getValue(values, 'userData.dateOfBirth');

	const resolveNextScreen = (cvDoc) => {
		const dateFeedback = getDateFormatFeedback(cvDoc);
		const timeline = getTimelineData(cvDoc, dateOfBirth);
		setDateFormatFeedback(dateFeedback);
		setTimelineData(timeline);

		if (dateFeedback) return 'dateFormatWarning';
		if (timeline) return 'gapClarification';
		return 'completion';
	};

	const handleInput = ({ target }) => {
		const { name, value } = target;

		if (name === 'userDocuments' && Array.isArray(value)) {
			const uploadedCV = value.find((doc) => doc?.type === 'cv');
			const uploadedCriminalRecord = value.find((doc) => doc?.type === 'model-596-1');

			const existing = values.userDocuments || [];
			const merged = existing.filter(
				(doc) => doc?.type !== 'cv' && doc?.type !== 'model-596-1',
			);
			if (uploadedCV) merged.unshift(uploadedCV);
			if (uploadedCriminalRecord) merged.splice(1, 0, uploadedCriminalRecord);

			if (uploadedCV) {
				resolveNextScreen(uploadedCV);
			}

			if (uploadedCV && uploadedCriminalRecord) {
				const nextScreen = resolveNextScreen(uploadedCV);
				setTimeout(() => setCurrentScreen(nextScreen), 100);
			}

			onChange({ target: { name: 'userDocuments', value: merged } });
			return;
		}

		onChange({ target });
	};

	const allGapsCovered = timelineData
		? timelineData.filter((e) => e.type === 'gap').every((gap) => isGapProofAccepted(findGapProof(userDocuments, gap)))
		: true;

	const screens = {
		welcome: {
			next: () => setCurrentScreen('cvUpload'),
			component: WelcomePage,
			canContinue: true,
		},
		cvUpload: {
			previous: () => setCurrentScreen('welcome'),
			next: () => {
				if (dateFormatFeedback || timelineData) {
					setCurrentScreen(dateFormatFeedback ? 'dateFormatWarning' : 'gapClarification');
					return;
				}
				setCurrentScreen(resolveNextScreen(cvDocument));
			},
			component: CVUploadPage,
			canContinue: !!cvDocument && !!criminalRecordDocument,
		},
		dateFormatWarning: {
			previous: () => setCurrentScreen('cvUpload'),
			component: DateFormatWarningPage,
			canContinue: false,
		},
		gapClarification: {
			previous: () => setCurrentScreen(dateFormatFeedback ? 'dateFormatWarning' : 'cvUpload'),
			next: () => setCurrentScreen('completion'),
			component: GapTimelinePage,
			canContinue: allGapsCovered,
		},
		completion: {
			previous: () => {
				if (timelineData) return setCurrentScreen('gapClarification');
				if (dateFormatFeedback) return setCurrentScreen('dateFormatWarning');
				return setCurrentScreen('cvUpload');
			},
			next: (event) => navigationProps.next(event),
			component: CompletionPage,
			canContinue: true,
		},
	};

	const screen = screens[currentScreen] || screens.welcome;
	const screenProps = {
		text,
		values,
		onChange,
		onInput: handleInput,
		uploadUrl,
		disabled,
		useCamera,
		cameraIncludes,
		cameraRotationAngle,
		Registry,
		timelineData,
		dateFormatFeedback,
	};

	return (
		<SectionContainer
			background={background}
			Navigation={
				<SectionNav
					{...navigationProps}
					hasPrevious={!!screen.previous}
					hasNext={currentScreen !== 'completion' || navigationProps.hasNext}
					canContinue={!!screen.canContinue}
					next={screen.next}
					previous={screen.previous}
					selectedLanguage={selectedLanguage}
				/>
			}
		>
			{screen && <screen.component {...screenProps} />}
		</SectionContainer>
	);
};

BackgroundCheckSection.type = 'BackgroundCheckSection';
BackgroundCheckSection.hasNavigation = true;
BackgroundCheckSection.spec = [
	{
		name: 'BackgroundCheckSection',
		key: 'BackgroundCheckSection',
		title: 'Background Check',
		translatable: ['title', 'navigation.previousLabel', 'navigation.nextLabel', 'navigation.submitLabel'],
		allowedFields: [],
		fields: [
			{
				type: 'TextField',
				name: 'title',
				label: 'Section title',
			},
			{
				type: 'Checkbox',
				name: 'useCamera',
				label: 'Force camera only (kiosk environments only — requires device to be registered)',
			},
		],
		defaultProperties: {
			title: 'Background Check',
			refDataMap: {
				'userData.firstName': 'user.firstName',
				'userData.lastName': 'user.lastName',
				'userData.dateOfBirth': 'user.userData.dateOfBirth',
			},
		},
	},
];

export default BackgroundCheckSection;
